import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Calculator, FlaskConical, Printer, RotateCcw, Send } from 'lucide-react'
import { MANUAL_DECISIONS, PASS_DECISIONS, PASS_MODES, type PassDecision } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Modal,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  Tabs,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import {
  DECISION_VARIANT,
  RANKING_VARIANT,
  RESULTS_KEY,
  fmt,
  useComparison,
  useRanking,
  useResultsOverview,
  type EntryView,
  type RankingDetail,
  type Simulation,
} from '../../lib/results'
import { RankingTable } from './RankingTable'
import { ApprovalPanel } from './ApprovalPanel'

type Tab = 'ranking' | 'admissible' | 'simulation' | 'compare'

/**
 * Résultats d'une étape (cahier §9.7-9.8) : simulation, calcul officiel en
 * versions, tableau classé, ex æquo à trancher, liste des admissibles,
 * comparaison de versions, transmission au contrôle.
 */
export function StageResultsPage() {
  const { stageId = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const overview = useResultsOverview(stageId)
  const o = overview.data
  const current = o?.versions.find((v) => v.status !== 'superseded') ?? o?.versions[0] ?? null
  const [selected, setSelected] = useState<string | null>(null)
  const rankingId = selected ?? current?.id ?? null
  const ranking = useRanking(rankingId)
  const [tab, setTab] = useState<Tab>('ranking')
  const [simulation, setSimulation] = useState<Simulation | null>(null)
  const [simRules, setSimRules] = useState<'validated' | 'draft'>('validated')
  const [compareWith, setCompareWith] = useState<string | null>(null)
  const comparison = useComparison(rankingId, compareWith)
  const [modal, setModal] = useState<
    null | 'compute' | 'submit' | 'return' | { resolve: EntryView }
  >(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: RESULTS_KEY })
  const simulate = useMutation({
    mutationFn: () =>
      session.api.post<Simulation>(`/admin/stages/${stageId}/results/simulate`, {
        rules: simRules,
      }),
    onSuccess: (data) => {
      setSimulation(data)
      setTab('simulation')
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const compute = useMutation({
    mutationFn: (reason?: string) =>
      session.api.post<RankingDetail>(`/admin/stages/${stageId}/rankings`, {
        reason: reason ?? null,
      }),
    onSuccess: async (data) => {
      toast.success(`Classement v${data.version} calculé`)
      setModal(null)
      setSelected(null)
      setTab('ranking')
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const transition = useMutation({
    mutationFn: ({ kind, reason }: { kind: 'submit' | 'return'; reason?: string }) =>
      session.api
        .post(`/admin/rankings/${rankingId}/${kind}`, reason ? { reason } : {})
        .then(() => kind),
    onSuccess: async (kind) => {
      toast.success(
        kind === 'submit' ? 'Classement transmis au contrôle' : 'Classement renvoyé en brouillon'
      )
      setModal(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (overview.isError) return <Alert tone="danger">{errorMessage(overview.error)}</Alert>
  if (!o)
    return (
      <Card>
        <CardBody>Chargement…</CardBody>
      </Card>
    )
  const r = ranking.data
  const canCompute = o.can.calculate && o.blockers.length === 0
  const others = o.versions.filter((v) => v.id !== rankingId)

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/resultats" className="link">
            Résultats et classements
          </Link>
        }
        title={`${o.stage.label} — ${o.competition.title}`}
        description={
          <>
            <span className="mono">{o.competition.reference}</span> · étape {o.stage.position} ·{' '}
            {o.stage.statusLabel}
          </>
        }
        actions={
          o.can.calculate && (
            <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {o.rules.draft !== null && (
                <Select
                  label="Règles simulées"
                  value={simRules}
                  onChange={(e) => setSimRules(e.target.value as 'validated' | 'draft')}
                  options={[
                    { value: 'validated', label: `Validées (v${o.rules.validated ?? '—'})` },
                    { value: 'draft', label: `Brouillon (v${o.rules.draft})` },
                  ]}
                />
              )}
              <Button
                icon={<FlaskConical />}
                loading={simulate.isPending}
                disabled={simRules === 'validated' && o.rules.validated === null}
                onClick={() => simulate.mutate()}
              >
                Simuler
              </Button>
              <Button
                variant="primary"
                icon={<Calculator />}
                disabled={!canCompute}
                onClick={() =>
                  o.versions.some((v) => v.status !== 'superseded')
                    ? setModal('compute')
                    : compute.mutate(undefined)
                }
              >
                {o.versions.length ? 'Recalculer' : 'Calculer le classement'}
              </Button>
            </span>
          )
        }
      />

      <StatGrid>
        <Stat label="Candidats" value={o.population} />
        <Stat label="Relevés validés" value={`${o.sheets.validated} / ${o.sheets.total}`} />
        <Stat label="Règles validées" value={o.rules.validated ? `v${o.rules.validated}` : '—'} />
        <Stat
          label="Classement"
          value={current ? `v${current.version}` : '—'}
          hint={current?.statusLabel}
        />
      </StatGrid>

      {o.can.calculate &&
        o.blockers.length > 0 &&
        !o.versions.some((v) => ['approved', 'published'].includes(v.status)) && (
          <Alert tone="warning" title="Avant le calcul officiel">
            <ul className="sel-issues">
              {o.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </Alert>
        )}
      {o.stage.anonymous && (
        <Alert tone="info">Anonymat non levé : les candidats apparaissent sous leur code.</Alert>
      )}

      <Tabs
        label="Résultats"
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={[
          { value: 'ranking', label: 'Classement' },
          { value: 'admissible', label: 'Liste des admis' },
          ...(simulation ? [{ value: 'simulation', label: 'Simulation' }] : []),
          ...(o.versions.length > 1 ? [{ value: 'compare', label: 'Comparer les versions' }] : []),
        ]}
      />

      {(tab === 'ranking' || tab === 'admissible') && !current && (
        <Card>
          <CardBody>
            <p className="muted" style={{ margin: 0 }}>
              Aucun classement calculé. Lancez une simulation pour vérifier les règles, puis le
              calcul officiel une fois tous les relevés validés.
            </p>
          </CardBody>
        </Card>
      )}

      {tab === 'ranking' && r && <ApprovalPanel key={`ap-${r.id}`} ranking={r} />}
      {tab === 'ranking' && r && (
        <Card>
          <CardHeader
            title={`Classement v${r.version}`}
            actions={
              <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {o.versions.length > 1 && (
                  <Select
                    label="Version"
                    value={rankingId ?? ''}
                    onChange={(e) => setSelected(e.target.value)}
                    options={o.versions.map((v) => ({
                      value: v.id,
                      label: `v${v.version} — ${v.statusLabel}`,
                    }))}
                  />
                )}
                {r.can.submit && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Send />}
                    disabled={r.summary.pending > 0}
                    onClick={() => setModal('submit')}
                  >
                    Transmettre au contrôle
                  </Button>
                )}
                {r.can.returnToDraft && (
                  <Button size="sm" icon={<RotateCcw />} onClick={() => setModal('return')}>
                    Renvoyer en brouillon
                  </Button>
                )}
              </span>
            }
          />
          <CardBody>
            <div className="ranking-meta">
              <Badge variant={RANKING_VARIANT[r.status]}>{r.statusLabel}</Badge>
              <span className="muted">
                Règles v{r.ruleVersion} · {PASS_MODES[r.rules.mode]}
                {r.rules.threshold !== null && ` · seuil ${fmt(r.rules.threshold)}`}
                {r.rules.quota !== null && ` · ${r.rules.quota} place(s)`}
                {r.rules.byCategory && ' · par catégorie'} · calculé par {r.computedBy} le{' '}
                {when(r.computedAt)}
              </span>
              {r.reason && <span className="muted">Motif du recalcul : {r.reason}</span>}
            </div>
            <SummaryBar summary={r.summary} />
            {r.summary.pending > 0 && r.can.resolve && (
              <Alert tone="warning">
                {r.summary.pending} ligne(s) relèvent d’une décision humaine (ex æquo à la limite
                des places, cas non prévu par le règlement). Chaque décision est motivée et tracée.
              </Alert>
            )}
            <RankingTable
              key={r.id}
              entries={r.entries}
              disciplines={r.disciplines}
              byCategory={r.rules.byCategory}
              onResolve={r.can.resolve ? (entry) => setModal({ resolve: entry }) : undefined}
            />
          </CardBody>
        </Card>
      )}

      {tab === 'admissible' && r && (
        <AdmittedList ranking={r} title={`${o.stage.label} — ${o.competition.title}`} />
      )}

      {tab === 'simulation' && simulation && (
        <Card>
          <CardHeader
            title={`Simulation — règles ${simulation.rulesStatus === 'draft' ? 'en brouillon' : 'validées'} v${simulation.ruleVersion}`}
          />
          <CardBody>
            <Alert tone="info">Simulation : rien n’est enregistré.</Alert>
            {simulation.warnings.map((w) => (
              <Alert key={w} tone="warning">
                {w}
              </Alert>
            ))}
            <SummaryBar summary={simulation.summary} />
            <RankingTable
              entries={simulation.entries}
              disciplines={simulation.disciplines}
              byCategory={false}
            />
          </CardBody>
        </Card>
      )}

      {tab === 'compare' && (
        <Card>
          <CardHeader
            title={`Comparer v${r?.version ?? '—'} avec…`}
            actions={
              <Select
                label="Version de référence"
                placeholder="— Choisir —"
                value={compareWith ?? ''}
                onChange={(e) => setCompareWith(e.target.value || null)}
                options={others.map((v) => ({
                  value: v.id,
                  label: `v${v.version} — ${v.statusLabel}`,
                }))}
              />
            }
          />
          <CardBody>
            {!compareWith && <p className="muted">Choisissez une version à comparer.</p>}
            {comparison.data && (
              <div className="tbl-wrap">
                <table className="tbl">
                  <caption className="sr-only">Différences entre versions</caption>
                  <thead>
                    <tr>
                      <th scope="col">Candidat</th>
                      <th scope="col">v{comparison.data.before.version}</th>
                      <th scope="col">v{comparison.data.after.version}</th>
                      <th scope="col">Changement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.data.diff.map((d) => (
                      <tr key={d.key}>
                        <th scope="row">{d.label}</th>
                        <td>
                          {d.before
                            ? `${d.before.rank ?? '—'}e · ${fmt(d.before.score)} · ${PASS_DECISIONS[d.before.decision]}`
                            : '—'}
                        </td>
                        <td>
                          {d.after
                            ? `${d.after.rank ?? '—'}e · ${fmt(d.after.score)} · ${PASS_DECISIONS[d.after.decision]}`
                            : '—'}
                        </td>
                        <td>
                          {d.changes
                            .map(
                              (c) =>
                                ({
                                  rank: 'rang',
                                  score: 'note',
                                  decision: 'décision',
                                  added: 'ajouté',
                                  removed: 'retiré',
                                })[c]
                            )
                            .join(', ')}
                        </td>
                      </tr>
                    ))}
                    {comparison.data.diff.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted">
                          Aucune différence.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {modal === 'compute' && (
        <ReasonModal
          title="Recalculer le classement ?"
          message="Une nouvelle version est créée ; la version actuelle est conservée (remplacée) et reste comparable."
          confirm="Recalculer"
          loading={compute.isPending}
          onClose={() => setModal(null)}
          onConfirm={(reason) => compute.mutate(reason)}
        />
      )}
      {typeof modal === 'object' && modal && r && (
        <ResolveModal
          entry={modal.resolve}
          supplementary={r.rules.supplementary.enabled}
          onClose={() => setModal(null)}
          onDone={async () => {
            setModal(null)
            await invalidate()
          }}
        />
      )}
      <ConfirmModal
        open={modal === 'submit'}
        onClose={() => setModal(null)}
        title="Transmettre le classement au contrôle ?"
        message="Il ne sera plus modifiable, sauf renvoi motivé en brouillon. L’approbation relève de l’autorité de validation."
        confirmLabel="Transmettre"
        onConfirm={() => transition.mutateAsync({ kind: 'submit' }).then(() => undefined)}
      />
      <ConfirmModal
        open={modal === 'return'}
        onClose={() => setModal(null)}
        title="Renvoyer le classement en brouillon ?"
        confirmLabel="Renvoyer"
        reasonRequired
        reasonLabel="Motif"
        reasonMinLength={10}
        onConfirm={(reason) =>
          transition.mutateAsync({ kind: 'return', reason }).then(() => undefined)
        }
      />
    </>
  )
}

function SummaryBar({ summary }: { summary: { counts: Record<PassDecision, number> } }) {
  const order: PassDecision[] = [
    'passed',
    'supplementary',
    'pending',
    'failed',
    'eliminated',
    'absent',
    'incomplete',
  ]
  return (
    <div className="results-summary">
      {order
        .filter((d) => summary.counts[d])
        .map((d) => (
          <Badge key={d} variant={DECISION_VARIANT[d]}>
            {PASS_DECISIONS[d]} : {summary.counts[d]}
          </Badge>
        ))}
    </div>
  )
}

/** Liste des admis (et liste complémentaire), imprimable. */
function AdmittedList({ ranking, title }: { ranking: RankingDetail; title: string }) {
  const passed = ranking.entries.filter((e) => e.decision === 'passed')
  const extra = ranking.entries.filter((e) => e.decision === 'supplementary')
  const list = (items: EntryView[]) => (
    <ol className="admitted-list">
      {items.map((e) => (
        <li key={e.id}>
          <span>{e.name ?? e.label}</span>
          <span className="mono muted">{e.name ? e.applicationNumber : ''}</span>
          <span className="num">{fmt(e.score)}</span>
        </li>
      ))}
    </ol>
  )
  return (
    <Card className="printable">
      <CardHeader
        title={`Admis — ${title} (v${ranking.version}, ${ranking.statusLabel.toLowerCase()})`}
        actions={
          <Button size="sm" icon={<Printer />} onClick={() => window.print()}>
            Imprimer
          </Button>
        }
      />
      <CardBody>
        {ranking.status === 'draft' && (
          <Alert tone="info">
            Document de travail : le classement n’est ni contrôlé ni approuvé.
          </Alert>
        )}
        <h3 className="sel-section-title">Liste principale ({passed.length})</h3>
        {passed.length ? list(passed) : <p className="muted">Aucun admis.</p>}
        {ranking.rules.supplementary.enabled && (
          <>
            <h3 className="sel-section-title">Liste complémentaire ({extra.length})</h3>
            {extra.length ? list(extra) : <p className="muted">Personne.</p>}
          </>
        )}
      </CardBody>
    </Card>
  )
}

function ResolveModal({
  entry,
  supplementary,
  onClose,
  onDone,
}: {
  entry: EntryView
  supplementary: boolean
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const { session } = useAuth()
  const toast = useToast()
  const [form, setForm] = useState({ decision: '' as PassDecision | '', reason: '' })
  const save = useMutation({
    mutationFn: () =>
      session.api.post(`/admin/ranking-entries/${entry.id}/decision`, {
        decision: form.decision,
        reason: form.reason.trim(),
      }),
    onSuccess: async () => {
      toast.success('Décision enregistrée')
      await onDone()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  return (
    <Modal
      open
      onClose={onClose}
      title={`Décision — ${entry.name ?? entry.label}`}
      description={entry.reasons.join(' ')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!form.decision || form.reason.trim().length < 10}
            onClick={() => save.mutate()}
          >
            Enregistrer la décision
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        <p style={{ margin: 0 }}>
          Rang {entry.rank ?? '—'} · note {fmt(entry.score)}
          {entry.tie && ' · ex æquo'}
        </p>
        <Select
          label="Décision"
          required
          placeholder="— Choisir —"
          value={form.decision}
          options={MANUAL_DECISIONS.filter((d) => supplementary || d !== 'supplementary').map(
            (d) => ({
              value: d,
              label: PASS_DECISIONS[d],
            })
          )}
          onChange={(e) => setForm({ ...form, decision: e.target.value as PassDecision })}
        />
        <Textarea
          label="Motif"
          required
          rows={3}
          hint="Fondement de la décision (article du règlement, délibération du jury…). 10 caractères minimum."
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </div>
    </Modal>
  )
}

function ReasonModal({
  title,
  message,
  confirm,
  loading,
  onClose,
  onConfirm,
}: {
  title: string
  message: string
  confirm: string
  loading: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={message}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={reason.trim().length < 10}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <Textarea
        label="Motif du recalcul"
        required
        rows={3}
        hint="10 caractères minimum."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  )
}
